import React, { useCallback, useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { Formik, FormikHelpers } from 'formik';
import { observer } from 'mobx-react';

import { LINKS_COLOR, PALE_GREY } from '../../../../theme';
import { formInitialValuesSmallScreen, validationSchemaSmallScreen } from '../formikConstants';
import { ReviewFormType, ReviewFormValues } from '../types';
import { SmallResolutionForm } from './SmallResolutionForm';
import { SCREENS_TRANSITIONS } from './constants';
import { DrivingSchoolsType } from '../constants';
import stores from '../../../../stores';
import { FormState } from './enums';

const Header = styled.div`
  height: 63px;
  display: flex;
  color: #fff;
  border-bottom: 1px solid ${PALE_GREY};
`;

const LogoWrap = styled.div`
  background: #ffcb05;
  width: 72px;
  min-width: 72px;
  height: 100%;
  padding: 8px 12px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Logo = styled.img`
  max-width: 100%;
  max-height: 100%;
`;

const HeaderText = styled.div`
  font-size: 16px;
  font-weight: 800;
  line-height: 1.38;
  letter-spacing: 0.2px;
  color: #003da5;
  padding: 8px 12px;
  background-color: ${({ theme }) => theme.drivingSchoolTypeColor};
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
`;

const Body = styled.div`
  padding: 16px 24px 24px;

  a {
    color: ${LINKS_COLOR};
  }
`;

const SmallResolution = observer(({
  reviewTextRef,
  firstName,
  examCenterTitle,
  examCenterLogo,
  isCustomTheme,
  onFormSubmit,
  email,
}: ReviewFormType) => {
  const theme = useTheme();

  const [activeScreen, setActiveScreen] = useState(FormState.Stars);
  const [nextScreen, setNextScreen] = useState(SCREENS_TRANSITIONS[activeScreen].next);
  const isLastScreen = !nextScreen;

  const onBackButtonClick = useCallback((e) => {
    const prevScreen = SCREENS_TRANSITIONS[activeScreen].prev;

    global.gtag('event', `exam-center-mobile-form-${activeScreen}-back-button-click`, {
      event_category: 'Exam center review mobile',
      event_label: `${activeScreen} screen back button click`,
    });

    if (prevScreen) {
      e.preventDefault();
      setActiveScreen(prevScreen);
      global.history.pushState(null, null, window.location.href);
    } else {
      global.history.back();
    }
  }, [activeScreen]);

  useEffect(() => {
    global.history.pushState(null, null, window.location.href);
  }, []);

  useEffect(() => {
    window.addEventListener('popstate', onBackButtonClick);

    return () => window.removeEventListener('popstate', onBackButtonClick);
  }, [activeScreen]);

  const onSubmit = async (values, {
    setErrors, setSubmitting, setStatus, setTouched,
  }: FormikHelpers<ReviewFormValues>): Promise<void> => {
    global.gtag('event', `exam-center-mobile-form-${activeScreen}-button-click`, {
      event_category: 'Exam center review mobile',
      event_label: `${activeScreen} screen button click`,
    });

    if (isLastScreen) {
      await onFormSubmit(values, { setErrors, setSubmitting } as FormikHelpers<ReviewFormValues>);

      const { drivingSchoolType, helpSearchSchool } = values;
      if (drivingSchoolType !== DrivingSchoolsType.TRADITIONAL && helpSearchSchool === '1') {
        stores.leadsStore.create({
          lead: {
            phone: values.phone,
            email,
            codepostal: values.zipcode,
            message: values.message,
          },
        }, setStatus, setSubmitting);
      }
    } else {
      setActiveScreen(nextScreen);
      setTouched({});
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header>
        <LogoWrap>
          <Logo src={isCustomTheme ? theme.imagePath : examCenterLogo} />
        </LogoWrap>
        <HeaderText>Votre avis compte pour nous !</HeaderText>
      </Header>
      <Body>
        <Formik
          initialValues={formInitialValuesSmallScreen}
          onSubmit={onSubmit}
          validationSchema={validationSchemaSmallScreen[activeScreen]}
        >
          <SmallResolutionForm
            reviewTextRef={reviewTextRef}
            firstName={firstName}
            examCenterTitle={examCenterTitle}
            examCenterLogo={examCenterLogo}
            activeScreen={activeScreen}
            setNextScreen={setNextScreen}
            isLastScreen={isLastScreen}
          />
        </Formik>
      </Body>
    </>
  );
});

export { SmallResolution };
